// Sanitized hast → React. No HTML strings: React builds the elements and escapes text.
// External links open in the normal browser (SPEC §9) with rel="noopener noreferrer".
import { createContext, useContext, useEffect, useId, useRef, useState, type ComponentProps } from 'react';
import DOMPurify from 'dompurify';
import { toJsxRuntime } from 'hast-util-to-jsx-runtime';
import { Fragment, jsx, jsxs } from 'react/jsx-runtime';
import type { Root } from 'hast';

/** How a document's links behave where it's shown (relative links, anchors). */
export type LinkTarget = { href: string; external: boolean; onNavigate?: () => void };
export type LinkResolver = (href: string) => LinkTarget;
export const LinkContext = createContext<LinkResolver | null>(null);

function Link(props: ComponentProps<'a'>) {
  const resolve = useContext(LinkContext);
  const raw = typeof props.href === 'string' ? props.href : '';
  const t: LinkTarget = resolve ? resolve(raw) : { href: raw, external: /^(https?:|mailto:)/i.test(raw) };
  if (t.external) return <a {...props} href={t.href} target="_blank" rel="noopener noreferrer" />;
  return (
    <a {...props} href={t.href} onClick={(e) => {
      if (!t.onNavigate || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      t.onNavigate();
    }} />
  );
}

/** GitHub suggestion blocks in comment bodies get a label (React-level, no HTML). */
function Pre(props: ComponentProps<'pre'>) {
  const child = props.children as { props?: { className?: string } } | undefined;
  if (child?.props?.className?.includes('language-suggestion')) {
    return <div className="hq-suggestion"><div className="hq-suggestion__label">Suggested change</div><pre {...props} /></div>;
  }
  return <pre {...props} />;
}

export function Rendered({ hast }: { hast: Root }) {
  return toJsxRuntime(hast, { Fragment, jsx, jsxs, components: { a: Link, pre: Pre }, passKeys: true });
}

/**
 * Mermaid diagrams are taken out of the HTML pipeline and drawn here with
 * securityLevel 'strict' set explicitly (SPEC §7). Loaded only when a document has one.
 */
export function Mermaid({ source }: { source: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const id = `mmd-${useId().replace(/[^a-z0-9]/gi, '')}`;
  const [error, setError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mermaid = (await import('mermaid')).default;
        // htmlLabels off: labels are SVG <text>, so the SVG sanitizer below can keep them
        // (HTML labels live in <foreignObject>, which the SVG profile removes).
        mermaid.initialize({
          startOnLoad: false, securityLevel: 'strict', theme: 'neutral', htmlLabels: false,
          flowchart: { htmlLabels: false }, fontFamily: 'Inter Tight, system-ui, sans-serif',
        });
        // Mermaid writes label HTML into the page while measuring text, before any sanitizing,
        // so HTML tags never reach it. Arrows like --> or <--> aren't tags and stay.
        const { svg } = await mermaid.render(id, stripHtmlTags(source));
        // Sanitize Mermaid's output too (SVG profile) before it touches the DOM.
        const clean = DOMPurify.sanitize(svg, { USE_PROFILES: { svg: true, svgFilters: true }, ADD_TAGS: ['style'] });
        if (!cancelled && ref.current) ref.current.innerHTML = clean;
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [id, source]);
  if (error) {
    return (
      <div className="hq-mermaid hq-mermaid--error">
        <p className="hq-note">Couldn’t draw this diagram. Its source:</p>
        <pre><code>{source}</code></pre>
      </div>
    );
  }
  return <div className="hq-mermaid" ref={ref} role="img" aria-label="Diagram" />;
}

/** Remove anything that looks like an HTML tag from Mermaid source (see Mermaid above). */
export function stripHtmlTags(source: string): string {
  return source.replace(/<\/?[a-z!][^<>]*>/gi, '');
}
